import { StravaActivitySummary } from "../types";
import styles from "./activity-card.module.css";

interface ActivityCardProps {
  activity: StravaActivitySummary;
  onSelect: (activityId: number) => void;
  isSelected?: boolean;
}

/**
 * Card component for displaying a Strava activity in a selection list.
 * Shows activity metadata and a mini SVG route preview from the summary polyline.
 */
export const ActivityCard = ({
  activity,
  onSelect,
  isSelected = false,
}: ActivityCardProps) => {
  const formattedDistance = formatDistance(activity.distance);
  const formattedElevation = formatElevation(activity.total_elevation_gain);
  const formattedDate = formatDate(activity.start_date_local);
  const formattedDuration = formatDuration(activity.moving_time);
  const activityIcon = getActivityIcon(activity.type);

  const handleClick = () => {
    onSelect(activity.id);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onSelect(activity.id);
    }
  };

  return (
    <div
      className={`${styles.card} ${isSelected ? styles.selected : ""}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      aria-pressed={isSelected}
    >
      <div className={styles.content}>
        <div className={styles.header}>
          <span className={styles.icon}>{activityIcon}</span>
          <span className={styles.type}>{activity.type}</span>
          <span className={styles.date}>{formattedDate}</span>
        </div>

        <h3 className={styles.name}>{activity.name}</h3>

        <div className={styles.stats}>
          {activity.distance > 0 && (
            <div className={styles.stat}>
              <span className={styles.statValue}>{formattedDistance}</span>
              <span className={styles.statLabel}>Distance</span>
            </div>
          )}

          {activity.total_elevation_gain > 0 && (
            <div className={styles.stat}>
              <span className={styles.statValue}>{formattedElevation}</span>
              <span className={styles.statLabel}>Elevation</span>
            </div>
          )}

          {activity.moving_time > 0 && (
            <div className={styles.stat}>
              <span className={styles.statValue}>{formattedDuration}</span>
              <span className={styles.statLabel}>Time</span>
            </div>
          )}
        </div>
      </div>

      <div className={styles.preview}>
        <RoutePreview polyline={activity.summary_polyline} />
      </div>

      {isSelected && <div className={styles.selectedIndicator}>✓</div>}
    </div>
  );
};

/**
 * SVG route preview component that renders a decoded polyline.
 */
const RoutePreview = ({ polyline }: { polyline: string }) => {
  if (!polyline) {
    return <div className={styles.noRoute}>No route</div>;
  }

  const coordinates = decodePolyline(polyline);

  if (coordinates.length < 2) {
    return <div className={styles.noRoute}>No route</div>;
  }

  const pathData = coordinatesToSvgPath(coordinates);

  return (
    <svg
      viewBox="0 0 100 100"
      className={styles.routeSvg}
      preserveAspectRatio="xMidYMid meet"
    >
      <path
        d={pathData}
        fill="none"
        stroke="var(--color-strava-primary)"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

/**
 * Decode a Google-encoded polyline string into an array of [lat, lng] coordinates.
 * Uses the standard polyline decoding algorithm.
 * @see https://developers.google.com/maps/documentation/utilities/polylinealgorithm
 */
function decodePolyline(encoded: string): [number, number][] {
  const coordinates: [number, number][] = [];
  let index = 0;
  let lat = 0;
  let lng = 0;

  while (index < encoded.length) {
    // Decode latitude
    let shift = 0;
    let result = 0;
    let byte: number;

    do {
      byte = encoded.charCodeAt(index++) - 63;
      result |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20);

    const deltaLat = result & 1 ? ~(result >> 1) : result >> 1;
    lat += deltaLat;

    // Decode longitude
    shift = 0;
    result = 0;

    do {
      byte = encoded.charCodeAt(index++) - 63;
      result |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20);

    const deltaLng = result & 1 ? ~(result >> 1) : result >> 1;
    lng += deltaLng;

    coordinates.push([lat / 1e5, lng / 1e5]);
  }

  return coordinates;
}

/**
 * Convert [lat, lng] coordinates to an SVG path string.
 * Normalizes coordinates to fit within a 100x100 viewBox with padding.
 */
function coordinatesToSvgPath(coordinates: [number, number][]): string {
  if (coordinates.length === 0) return "";

  // Find bounds
  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLng = Infinity;
  let maxLng = -Infinity;

  for (const [lat, lng] of coordinates) {
    minLat = Math.min(minLat, lat);
    maxLat = Math.max(maxLat, lat);
    minLng = Math.min(minLng, lng);
    maxLng = Math.max(maxLng, lng);
  }

  // Add padding (10% on each side)
  const padding = 10;
  const viewSize = 100 - padding * 2;

  // Calculate scale to fit within viewBox while maintaining aspect ratio
  const latRange = maxLat - minLat || 0.0001;
  const lngRange = maxLng - minLng || 0.0001;
  const scale = Math.min(viewSize / latRange, viewSize / lngRange);

  // Center the path
  const centerX = padding + viewSize / 2;
  const centerY = padding + viewSize / 2;
  const midLat = (minLat + maxLat) / 2;
  const midLng = (minLng + maxLng) / 2;

  // Build SVG path
  const pathParts: string[] = [];

  for (let i = 0; i < coordinates.length; i++) {
    const [lat, lng] = coordinates[i];

    // Convert to SVG coordinates (flip Y axis since SVG Y increases downward)
    const x = centerX + (lng - midLng) * scale;
    const y = centerY - (lat - midLat) * scale;

    if (i === 0) {
      pathParts.push(`M ${x.toFixed(1)} ${y.toFixed(1)}`);
    } else {
      pathParts.push(`L ${x.toFixed(1)} ${y.toFixed(1)}`);
    }
  }

  return pathParts.join(" ");
}

/**
 * Format distance in meters to a human-readable string.
 */
function formatDistance(meters: number): string {
  if (!meters || meters === 0) return "—";

  if (meters >= 1000) {
    const km = meters / 1000;
    return `${km.toFixed(1)} km`;
  }

  return `${Math.round(meters)} m`;
}

/**
 * Format elevation gain in meters.
 */
function formatElevation(meters: number): string {
  if (!meters || meters === 0) return "—";
  return `${Math.round(meters)} m`;
}

/**
 * Format duration in seconds to a human-readable string.
 */
function formatDuration(seconds: number): string {
  if (!seconds || seconds === 0) return "—";

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }

  return `${minutes}m`;
}

/**
 * Format ISO date string to a readable format.
 */
function formatDate(isoString: string): string {
  try {
    const date = new Date(isoString);
    return date.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    });
  } catch {
    return "—";
  }
}

/**
 * Get an emoji icon for the activity type.
 */
function getActivityIcon(activityType: string): string {
  const type = activityType.toLowerCase();

  if (type.includes("run")) return "🏃";
  if (type.includes("ride") || type.includes("cycling") || type.includes("bike"))
    return "🚴";
  if (type.includes("swim")) return "🏊";
  if (type.includes("hike") || type.includes("walk")) return "🥾";
  if (type.includes("ski") || type.includes("snowboard")) return "⛷️";
  if (type.includes("kayak") || type.includes("canoe") || type.includes("paddle"))
    return "🛶";
  if (type.includes("row")) return "🚣";
  if (type.includes("golf")) return "⛳";
  if (type.includes("yoga") || type.includes("workout")) return "🧘";

  return "📍";
}
